import { currentOwnerKey } from "@/api/session";
import { type ReloginSecret, unlockSecret } from "@/unlock";
import { type ReactNode, useEffect, useState } from "react";
import { PIN_LENGTH, PinPad } from "./PinPad";

function attemptsMessage(attemptsLeft: number): string {
  const remaining =
    attemptsLeft === 1 ? "Te queda 1 intento" : `Te quedan ${attemptsLeft} intentos`;
  return `PIN incorrecto. ${remaining}: cuando se agoten se borra la contraseña guardada acá y vas a tener que entrar con usuario y contraseña.`;
}

export function PinVerify({
  ownerKey,
  title,
  hint,
  footer,
  disabled = false,
  onVerified,
  onWiped,
  onMissing,
}: {
  ownerKey?: string;
  title: string;
  hint: string;
  footer?: ReactNode;
  disabled?: boolean;
  onVerified: (secret: ReloginSecret) => void;
  onWiped: () => void;
  onMissing: () => void;
}) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (pin.length < PIN_LENGTH || busy || disabled) {
      return;
    }
    setBusy(true);
    unlockSecret(ownerKey ?? currentOwnerKey(), pin)
      .then((result) => {
        setPin("");
        if (result.ok) {
          onVerified(result.secret);
          return;
        }
        if (result.reason === "wrong-pin") {
          setError(attemptsMessage(result.attemptsLeft));
          return;
        }
        if (result.reason === "wiped") {
          onWiped();
          return;
        }
        onMissing();
      })
      .catch(() => {
        setPin("");
        setError("No pudimos leer el PIN guardado en este teléfono.");
      })
      .finally(() => setBusy(false));
  }, [pin, busy, disabled, ownerKey, onVerified, onWiped, onMissing]);

  return (
    <div className="pin-flow">
      <h2 className="t-h3">{title}</h2>
      <p className="t-small">{hint}</p>

      {error ? (
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      ) : null}

      {busy ? <p className="t-small">Verificando…</p> : null}

      <PinPad value={pin} onChange={setPin} disabled={busy || disabled} />

      {footer}
    </div>
  );
}
